/**
 * Sticky Add to Cart web component.
 *
 * Behavior:
 * - Hidden until the main buy_buttons element scrolls out of view above viewport.
 * - Mirrors price changes from the live #price-{section.id} element when variant changes.
 * - Mirrors disabled/enabled state from the main ATC button (out-of-stock variants).
 * - Submits the existing product form via form="<id>" attribute on the button.
 */

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._priceObserver = null;
    this._mainBtnObserver = null;
    this._handleFormSubmit = this._handleFormSubmit.bind(this);
  }

  connectedCallback() {
    // Defer to next frame so the page has rendered the target elements.
    requestAnimationFrame(() => this._init());
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (this._priceObserver) this._priceObserver.disconnect();
    if (this._mainBtnObserver) this._mainBtnObserver.disconnect();
    document.body.classList.remove('has-sticky-atc-visible');
    const form = this._getForm();
    if (form) form.removeEventListener('submit', this._handleFormSubmit);
  }

  _init() {
    const triggerSel = this.getAttribute('data-trigger-selector');
    const target = triggerSel ? document.querySelector(triggerSel) : null;

    if (!target) {
      // Fallback: trigger after scrolling 400px from top of page.
      this._initScrollFallback();
    } else {
      this._initIntersection(target);
    }

    this._initPriceMirror();
    this._initMainButtonMirror();
    this._initFormLoadingState();
  }

  _initIntersection(target) {
    // Show when target is OUT of viewport (i.e. scrolled past it OR not yet reached).
    // We want it visible only when user has scrolled PAST the target (target above viewport).
    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const rect = entry.boundingClientRect;
          const passed = rect.bottom < 0; // target is above viewport
          if (!entry.isIntersecting && passed) {
            this._show();
          } else {
            this._hide();
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px 0px 0px' }
    );
    this._observer.observe(target);
  }

  _initScrollFallback() {
    const onScroll = () => {
      if (window.scrollY > 600) this._show();
      else this._hide();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  _initPriceMirror() {
    const sourceSel = this.getAttribute('data-price-source');
    if (!sourceSel) return;
    const source = document.querySelector(sourceSel);
    if (!source) return;
    const dest = this.querySelector('[data-sticky-price]');
    if (!dest) return;

    const sync = () => {
      // Try to find a .price-item--sale or .price__regular inside source.
      const sale = source.querySelector(
        '.price-item--sale, .price-item--last, .price__regular .price-item'
      );
      const compare = source.querySelector('.price-item--regular, .price__sale .price-item--regular');
      if (sale && sale.textContent.trim()) {
        const now = dest.querySelector('.sticky-atc__price-now');
        if (now) now.textContent = sale.textContent.trim();
      }
      // Compare-at handling
      const wasEl = dest.querySelector('.sticky-atc__price-was');
      if (compare && compare.textContent.trim() && compare.offsetParent !== null) {
        const compareText = compare.textContent.trim();
        if (wasEl) {
          wasEl.textContent = compareText;
          wasEl.style.display = '';
        } else {
          const s = document.createElement('s');
          s.className = 'sticky-atc__price-was';
          s.textContent = compareText;
          dest.appendChild(s);
        }
      } else if (wasEl) {
        wasEl.style.display = 'none';
      }
    };

    this._priceObserver = new MutationObserver(sync);
    this._priceObserver.observe(source, { childList: true, subtree: true, characterData: true });
    sync();
  }

  _initMainButtonMirror() {
    // Find the original ATC button inside the product form.
    const formId = this.getAttribute('data-product-form-id');
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;
    const mainBtn = form.querySelector('[name="add"]:not([data-sticky-atc-button])');
    if (!mainBtn) return;
    const myBtn = this.querySelector('[data-sticky-atc-button]');
    const myLabel = this.querySelector('[data-sticky-atc-label]');
    if (!myBtn) return;

    const sync = () => {
      const disabled = mainBtn.disabled || mainBtn.getAttribute('aria-disabled') === 'true';
      myBtn.disabled = disabled;
      // Mirror label text (e.g. "Sold out", "Pre-order")
      const mainLabel = mainBtn.querySelector('span') || mainBtn;
      if (myLabel && mainLabel.textContent.trim()) {
        const txt = mainLabel.textContent.trim();
        // Keep our short label unless main says Sold out / Unavailable
        if (/sold out|unavailable|pre.?order/i.test(txt)) {
          myLabel.textContent = txt;
        } else {
          myLabel.textContent = 'Add to cart';
        }
      }
    };

    this._mainBtnObserver = new MutationObserver(sync);
    this._mainBtnObserver.observe(mainBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
      childList: true,
      subtree: true,
      characterData: true,
    });
    sync();
  }

  _initFormLoadingState() {
    const form = this._getForm();
    if (!form) return;
    form.addEventListener('submit', this._handleFormSubmit);
  }

  _handleFormSubmit() {
    const btn = this.querySelector('[data-sticky-atc-button]');
    if (!btn) return;
    btn.classList.add('is-loading');
    setTimeout(() => btn.classList.remove('is-loading'), 2500);
  }

  _getForm() {
    const formId = this.getAttribute('data-product-form-id');
    return formId ? document.getElementById(formId) : null;
  }

  _show() {
    if (this.classList.contains('is-visible')) return;
    this.classList.add('is-visible');
    this.removeAttribute('hidden');
    document.body.classList.add('has-sticky-atc-visible');
  }

  _hide() {
    if (!this.classList.contains('is-visible')) return;
    this.classList.remove('is-visible');
    document.body.classList.remove('has-sticky-atc-visible');
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
